/**
 * B76B lane B — the athlete-side boundary card, screens 1b and 1c.
 *
 * ONE component for both because the handoff says they are one thing:
 * "The B76 server 403 must never reach the eye; the client routes here first
 * (same card, same two lines, everywhere)." Two components would be two
 * chances for the two lines to drift apart.
 *
 * The variants differ only in frame:
 *   · `inline` (1b) — sits IN FLOW under the Rosa row on /chat. Headline 24px.
 *   · `full`   (1c) — full-screen, vertically centred on --cream, wherever a
 *                     coach lands on an athlete surface. Headline 30px, plus a
 *                     kicker above and a quiet "Back to your team" below.
 *
 * ⚖️ THIS IS A BOUNDARY, NOT A FAILURE. Handoff §1b: "Never a lock icon, never
 * an error tone." No --danger, no lock, no apology, no "you can't". The card
 * names what the athlete side IS and what the coaching side already gives, and
 * the CTA is an offer. A coach who never buys should be able to read this
 * every day without feeling refused.
 *
 * Copy is VERBATIM from `lib/coachAthletePaywallCopy.ts` (founder-ratified);
 * this file authors none of it.
 */

import { Link } from 'react-router-dom';
import { COACHES } from '../../lib/coaches';
import styles from './AthleteBoundary.module.css';

export interface AthleteBoundaryProps {
  /** `inline` = 1b (in flow on /chat); `full` = 1c (full-screen). */
  variant: 'inline' | 'full';
  /** Uppercase kicker above the headline (1c only). */
  kicker?: string;
  /** Instrument Serif italic headline. */
  headline: string;
  /** Body copy. */
  body: string;
  /** Render the five muted coach marks between body and CTA (1b). */
  coachMarks?: boolean;
  /** Full-width marine pill label. */
  cta: string;
  /** Opens the athlete membership sheet (2b) — see
   *  `contexts/AthleteMembershipSheetContext`. */
  onCta: () => void;
  /** Centred price line under the CTA. */
  subLine: string;
  /** Quiet marine link below the sub-line (1c). */
  backLink?: { label: string; to: string };
  /** Accessible label for the section. */
  ariaLabel: string;
}

/**
 * The five athlete coaches, muted to 0.35. Canonical taxonomy + order from
 * `lib/coaches.ts` (A · G · V · O · K), so this can never drift from the real
 * roster. Decorative: the names are already in the body copy above, so the
 * marks add nothing for a screen reader and are hidden from it.
 */
function CoachMarks(): JSX.Element {
  return (
    <div className={styles.marks} aria-hidden="true">
      {COACHES.map((coach) => (
        <span
          key={coach.id}
          className={styles.mark}
          style={{ background: coach.colorVar }}
        >
          {coach.initial}
        </span>
      ))}
    </div>
  );
}

export function AthleteBoundary({
  variant,
  kicker,
  headline,
  body,
  coachMarks = false,
  cta,
  onCta,
  subLine,
  backLink,
  ariaLabel,
}: AthleteBoundaryProps): JSX.Element {
  const card = (
    <section
      className={[styles.card, variant === 'full' ? styles.full : styles.inline].join(' ')}
      aria-label={ariaLabel}
    >
      {kicker ? <div className={styles.kicker}>{kicker}</div> : null}
      <h2 className={styles.headline}>{headline}</h2>
      <p className={styles.body}>{body}</p>
      {coachMarks ? <CoachMarks /> : null}
      <button type="button" className={styles.cta} onClick={onCta}>
        {cta}
      </button>
      <p className={styles.subLine}>{subLine}</p>
      {backLink ? (
        <Link to={backLink.to} className={styles.backLink}>
          {backLink.label}
        </Link>
      ) : null}
    </section>
  );

  // 1c is a full-screen state: the centring frame is part of the design, not
  // of the host route, so it travels with the card to every surface that
  // serves it.
  return variant === 'full' ? <div className={styles.screen}>{card}</div> : card;
}
